import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import Swal from "sweetalert2";
import { supabase } from "@/lib/supabaseClient";
import { uploadFileToVps, deleteFileFromVps } from "@/lib/vpsStorage";
import LoadingState from "@/components/base/LoadingState";
import ActivationModal from "./components/ActivationModal";
import QRCodeDisplay from "./components/QRCodeDisplay";

/* ─── Types ──────────────────────────────────────────────────── */
type InvitationData = {
  id: string;
  tenant_id: string;
  client_id: string;
  title: string;
  event_title: string | null;
  host_names: string | null;
  event_type: string;
  event_date: string | null;
  event_time: string | null;
  venue_name: string | null;
  venue_address: string | null;
  google_maps_url: string | null;
  dress_code: string | null;
  gift_registry_url: string | null;
  cover_image_url: string | null;
  photo_url: string | null;
  music_url: string | null;
  client_music_url: string | null;
  client_music_name: string | null;
  music_title: string | null;
  gallery_urls: string[];
  theme_key: string;
  template_id: string | null;
  custom_template_id: string | null;
  design_mode: string;
  invitation_mode: string;
  custom_template_url: string | null;
  ceremony_name: string | null;
  ceremony_address: string | null;
  ceremony_maps_url: string | null;
  ceremony_time: string | null;
  ceremony_location: string | null;
  reception_name: string | null;
  reception_address: string | null;
  reception_maps_url: string | null;
  welcome_message: string | null;
  thank_you_message: string | null;
  text_color: string;
  show_guests: boolean;
  rsvp_enabled: boolean;
  rsvp_message: string | null;
  is_published: boolean;
  public_slug: string;
};

type RsvpRow = {
  id: string;
  guest_name: string;
  phone: string | null;
  email: string | null;
  attendance_status: "yes" | "no" | "maybe";
  guest_count: number;
  message: string | null;
  created_at: string;
};

type InvTemplate = {
  id: string;
  name: string;
  background_url: string | null;
  preview_url: string | null;
  event_types: string[];
  text_color: string;
  overlay_color: string | null;
};

type ClientTemplate = {
  id: string;
  name: string;
  template_url: string;
  preview_url: string | null;
};

type MusicTrack = {
  id: string;
  name: string;
  artist: string | null;
  music_url: string;
  duration: string | null;
  genre: string | null;
};

const TABS = [
  { key: "template", label: "Diseño",    icon: "ri-palette-line" },
  { key: "event",    label: "Evento",    icon: "ri-calendar-event-line" },
  { key: "ceremony", label: "Ceremonia", icon: "ri-map-pin-line" },
  { key: "music",    label: "Música",    icon: "ri-music-2-line" },
  { key: "message",  label: "Mensajes",  icon: "ri-chat-heart-line" },
  { key: "share",    label: "Compartir", icon: "ri-share-line" },
  { key: "guests",   label: "Invitados", icon: "ri-group-line" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

/* ─── Music player ───────────────────────────────────────────── */
function MusicPlayer({ url, title }: { url: string; title?: string | null }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) { audio.pause(); setPlaying(false); }
    else { audio.play().then(() => setPlaying(true)).catch(() => {}); }
  };

  return (
    <div className="flex items-center gap-3 rounded-xl px-4 py-3 border border-[#C8A96E]/20 bg-[#C8A96E]/5">
      <audio ref={audioRef} src={url}
        onTimeUpdate={() => {
          const a = audioRef.current;
          if (a && a.duration) setProgress((a.currentTime / a.duration) * 100);
        }}
        onEnded={() => setPlaying(false)}
      />
      <button onClick={toggle} className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all cursor-pointer bg-[#C8A96E]/20 text-[#C8A96E]">
        <i className={playing ? "ri-pause-fill text-lg" : "ri-play-fill text-lg"} />
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium mb-1.5 text-[#0f172a] truncate">{title || "Música de fondo"}</p>
        <div className="h-1 rounded-full overflow-hidden bg-[#C8A96E]/20">
          <div className="h-full rounded-full transition-all bg-[#C8A96E]" style={{ width: `${progress}%` }} />
        </div>
      </div>
      <span className="text-xs text-slate-400 shrink-0">{playing ? "▶ Sonando" : "Pausa"}</span>
    </div>
  );
}

/* ─── Main page ──────────────────────────────────────────────── */
export default function PortalInvitationPage() {
  const { token } = useParams<{ token: string }>();
  const [tab, setTab]           = useState<TabKey>("template");
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [addonActive, setAddonActive] = useState(false);
  const [hasInvitation, setHasInvitation] = useState(false);
  const [form, setForm]         = useState<InvitationData | null>(null);
  const [dirty, setDirty]       = useState(false);
  const [saving, setSaving]     = useState(false);
  const [rsvps, setRsvps]       = useState<RsvpRow[]>([]);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState("Photography Studio");
  const [pendingUploadIds, setPendingUploadIds] = useState<string[]>([]);

  // Catalog data
  const [genericTemplates, setGenericTemplates] = useState<InvTemplate[]>([]);
  const [clientTemplates, setClientTemplates]   = useState<ClientTemplate[]>([]);
  const [musicCatalog, setMusicCatalog]         = useState<MusicTrack[]>([]);

  // Upload refs
  const coverInputRef = useRef<HTMLInputElement>(null);
  const customTplInputRef = useRef<HTMLInputElement>(null);
  const musicInputRef = useRef<HTMLInputElement>(null);
  const [uploadingCover, setUploadingCover]   = useState(false);
  const [uploadingMusic, setUploadingMusic]   = useState(false);

  // Share
  const shareUrl = form?.public_slug ? `${window.location.origin}/invite/${form.public_slug}` : "";

  /* ── Load ── */
  const load = useCallback(async () => {
    if (!token) { setError("No access token."); setLoading(false); return; }

    const { data: tok } = await supabase.from("client_access_tokens").select("*").eq("token", token).maybeSingle();
    if (!tok || tok.status !== "active") { setError("Enlace inválido o desactivado."); setLoading(false); return; }

    setTenantId(tok.tenant_id);
    setClientId(tok.client_id);

    const [tenantRes, addonRes, invRes, tplRes, ctplRes, musicRes] = await Promise.all([
      supabase.from("saas_tenants").select("name").eq("id", tok.tenant_id).maybeSingle(),
      supabase.from("client_addons").select("id, status").eq("tenant_id", tok.tenant_id).eq("client_id", tok.client_id).eq("addon_key", "digital_invitation").eq("status", "active").maybeSingle(),
      supabase.from("digital_invitations").select("*").eq("tenant_id", tok.tenant_id).eq("client_id", tok.client_id).maybeSingle(),
      supabase.from("invitation_templates").select("id, name, background_url, preview_url, event_types, text_color, overlay_color").or(`tenant_id.eq.${tok.tenant_id},tenant_id.is.null`).eq("is_active", true).order("sort_order"),
      supabase.from("client_invitation_templates").select("id, name, template_url, preview_url").eq("tenant_id", tok.tenant_id).eq("client_id", tok.client_id).eq("is_active", true),
      supabase.from("invitation_music").select("id, name, artist, music_url, duration, genre").eq("tenant_id", tok.tenant_id).eq("is_active", true).order("sort_order"),
    ]);

    if (tenantRes.data?.name) setTenantName(tenantRes.data.name);
    setAddonActive(!!addonRes.data);
    setGenericTemplates((tplRes.data ?? []) as InvTemplate[]);
    setClientTemplates((ctplRes.data ?? []) as ClientTemplate[]);
    setMusicCatalog((musicRes.data ?? []) as MusicTrack[]);

    if (invRes.data) {
      setHasInvitation(true);
      const inv = invRes.data as Record<string, unknown>;
      const invData: InvitationData = {
        id:                  inv.id as string,
        tenant_id:           inv.tenant_id as string,
        client_id:           inv.client_id as string,
        title:               (inv.title as string) || "",
        event_title:         inv.event_title as string | null,
        host_names:          inv.host_names as string | null,
        event_type:          (inv.event_type as string) || "Wedding",
        event_date:          inv.event_date as string | null,
        event_time:          inv.event_time as string | null,
        venue_name:          inv.venue_name as string | null,
        venue_address:       inv.venue_address as string | null,
        google_maps_url:     inv.google_maps_url as string | null,
        dress_code:          inv.dress_code as string | null,
        gift_registry_url:   inv.gift_registry_url as string | null,
        cover_image_url:     inv.cover_image_url as string | null,
        photo_url:           inv.photo_url as string | null,
        music_url:           inv.music_url as string | null,
        client_music_url:    inv.client_music_url as string | null,
        client_music_name:   inv.client_music_name as string | null,
        music_title:         inv.music_title as string | null,
        gallery_urls:        Array.isArray(inv.gallery_urls) ? inv.gallery_urls as string[] : [],
        theme_key:           (inv.theme_key as string) || "classic",
        template_id:         inv.template_id as string | null,
        custom_template_id:  inv.custom_template_id as string | null,
        design_mode:         (inv.design_mode as string) || "template",
        invitation_mode:     (inv.invitation_mode as string) || "generic_template",
        custom_template_url: inv.custom_template_url as string | null,
        ceremony_name:       inv.ceremony_name as string | null,
        ceremony_address:    inv.ceremony_address as string | null,
        ceremony_maps_url:   inv.ceremony_maps_url as string | null,
        ceremony_time:       inv.ceremony_time as string | null,
        ceremony_location:   inv.ceremony_location as string | null,
        reception_name:      inv.reception_name as string | null,
        reception_address:   inv.reception_address as string | null,
        reception_maps_url:  inv.reception_maps_url as string | null,
        welcome_message:     inv.welcome_message as string | null,
        thank_you_message:   inv.thank_you_message as string | null,
        text_color:          (inv.text_color as string) || "#ffffff",
        show_guests:         Boolean(inv.show_guests),
        rsvp_enabled:        Boolean(inv.rsvp_enabled),
        rsvp_message:        inv.rsvp_message as string | null,
        is_published:        Boolean(inv.is_published),
        public_slug:         (inv.public_slug as string) || "",
      };
      setForm(invData);

      const { data: rsvpRows } = await supabase
        .from("invitation_rsvps")
        .select("id, guest_name, phone, email, attendance_status, guest_count, message, created_at")
        .eq("invitation_id", inv.id as string)
        .order("created_at", { ascending: false });
      setRsvps((rsvpRows ?? []) as RsvpRow[]);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  /* ── Helpers ── */
  const hc = (patch: Partial<InvitationData>) => {
    setForm((prev) => (prev ? { ...prev, ...patch } : prev));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!form?.id || !tenantId) return;
    setSaving(true);
    const { error: upErr } = await supabase.from("digital_invitations").update({
      event_title:         form.event_title || null,
      host_names:          form.host_names || null,
      event_type:          form.event_type,
      event_date:          form.event_date || null,
      event_time:          form.event_time || null,
      venue_name:          form.venue_name || null,
      venue_address:       form.venue_address || null,
      google_maps_url:     form.google_maps_url || null,
      dress_code:          form.dress_code || null,
      gift_registry_url:   form.gift_registry_url || null,
      cover_image_url:     form.cover_image_url || null,
      photo_url:           form.photo_url || null,
      music_url:           form.music_url || null,
      client_music_url:    form.client_music_url || null,
      client_music_name:   form.client_music_name || null,
      music_title:         form.music_title || null,
      gallery_urls:        form.gallery_urls,
      theme_key:           form.theme_key,
      template_id:         form.template_id || null,
      custom_template_id:  form.custom_template_id || null,
      design_mode:         form.design_mode,
      invitation_mode:     form.invitation_mode,
      custom_template_url: form.custom_template_url || null,
      ceremony_name:       form.ceremony_name || null,
      ceremony_address:    form.ceremony_address || null,
      ceremony_maps_url:   form.ceremony_maps_url || null,
      ceremony_time:       form.ceremony_time || null,
      ceremony_location:   form.ceremony_location || null,
      reception_name:      form.reception_name || null,
      reception_address:   form.reception_address || null,
      reception_maps_url:  form.reception_maps_url || null,
      welcome_message:     form.welcome_message || null,
      thank_you_message:   form.thank_you_message || null,
      text_color:          form.text_color || "#ffffff",
      show_guests:         form.show_guests,
      rsvp_enabled:        form.rsvp_enabled,
      rsvp_message:        form.rsvp_message || null,
      is_published:        form.is_published,
      updated_at:          new Date().toISOString(),
    }).eq("id", form.id).eq("tenant_id", tenantId);
    setSaving(false);
    if (upErr) {
      // Cleanup uploaded files if DB save failed
      for (const fid of pendingUploadIds) {
        await deleteFileFromVps({ fileId: fid, tenantId, silent: true });
      }
      setPendingUploadIds([]);
      Swal.fire({ title: "Error", text: upErr.message, icon: "error", confirmButtonColor: "#C8A96E", background: "#ffffff", color: "#0f172a" });
    } else {
      setPendingUploadIds([]);
      setDirty(false);
      Swal.fire({ title: "¡Guardado!", icon: "success", confirmButtonColor: "#C8A96E", background: "#ffffff", color: "#0f172a", timer: 1500, showConfirmButton: false });
    }
  };

  const handlePublishToggle = async () => {
    if (!form?.id || !tenantId) return;
    const nv = !form.is_published;
    await supabase.from("digital_invitations").update({ is_published: nv, updated_at: new Date().toISOString() }).eq("id", form.id).eq("tenant_id", tenantId);
    hc({ is_published: nv });
    setDirty(false);
    Swal.fire({ title: nv ? "¡Publicada!" : "Despublicada", icon: "success", confirmButtonColor: "#C8A96E", background: "#ffffff", color: "#0f172a", timer: 1200, showConfirmButton: false });
  };

  const uploadFile = async (file: File, _pathPrefix: string): Promise<string | null> => {
    if (!form?.id || !tenantId) return null;
    const result = await uploadFileToVps({
      file,
      tenantId,
      folder: "invitations",
      allowedTypes: ["image/*", "audio/*"],
      maxSizeMB: 25,
    });
    if (!result.success || !result.url) {
      Swal.fire({ title: "Error al subir", text: result.error || "Upload failed", icon: "error", confirmButtonColor: "#C8A96E", background: "#fff", color: "#0f172a" });
      return null;
    }
    if (result.fileId) {
      setPendingUploadIds((prev) => [...prev, result.fileId!]);
    }
    return result.url;
  };

  const INPUT = "w-full rounded-lg px-3 py-2.5 text-sm border border-slate-200 text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors bg-white";
  const LABEL = "block text-xs font-medium text-slate-500 mb-1.5";

  const yesCount   = rsvps.filter((r) => r.attendance_status === "yes").reduce((a, b) => a + (b.guest_count || 1), 0);
  const noCount    = rsvps.filter((r) => r.attendance_status === "no").length;
  const maybeCount = rsvps.filter((r) => r.attendance_status === "maybe").length;

  /* ── Render ── */
  if (loading) return <LoadingState fullPage text="Cargando invitación…" />;
  if (error) return (
    <div className="min-h-screen bg-[#f3f7fb] flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
          <i className="ri-error-warning-line text-red-600 text-2xl" />
        </div>
        <h1 className="text-[#0f172a] text-xl font-bold mb-2">Error</h1>
        <p className="text-slate-500 text-sm">{error}</p>
      </div>
    </div>
  );

  /* Not active → activation flow */
  if (!addonActive) {
    return (
      <ActivationModal
        tenantId={tenantId ?? ""}
        clientId={clientId ?? ""}
        tenantName={tenantName}
        onActivated={() => { setLoading(true); load(); }}
      />
    );
  }

  /* No invitation created yet */
  if (!hasInvitation || !form) {
    return (
      <div className="min-h-screen bg-[#f3f7fb] flex items-center justify-center px-6">
        <div className="w-full max-w-md text-center space-y-5">
          <div className="w-20 h-20 mx-auto rounded-2xl bg-[#C8A96E]/10 flex items-center justify-center">
            <i className="ri-mail-send-line text-3xl text-[#C8A96E]" />
          </div>
          <h1 className="text-[#0f172a] text-xl font-bold">Invitación pendiente</h1>
          <p className="text-slate-500 text-sm">Tu fotógrafo aún no ha creado la invitación. Una vez creada, aparecerá aquí para que la personalices.</p>
          <Link to={`/portal/${token}`} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-xl px-4 py-2.5 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line"></i> Volver al portal
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f3f7fb]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#C8A96E]/10 flex items-center justify-center">
              <i className="ri-mail-send-line text-[#C8A96E] text-sm" />
            </div>
            <div>
              <p className="text-[#0f172a] font-semibold text-sm">Mi Invitación</p>
              <p className="text-slate-400 text-xs">{tenantName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {form.is_published && (
              <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600 text-xs font-medium border border-emerald-100">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                Publicada
              </span>
            )}
            <Link to={`/portal/${token}`} className="text-xs font-medium text-slate-400 hover:text-slate-600 transition-colors cursor-pointer flex items-center gap-1">
              <i className="ri-arrow-left-line"></i>
              <span className="hidden sm:inline">Portal</span>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 md:px-6 py-5 md:py-7 space-y-5">
        {/* Tab bar */}
        <div className="bg-white rounded-xl border border-slate-200 p-1 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex-1 min-w-[88px] flex items-center justify-center gap-1.5 py-2.5 px-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                tab === t.key
                  ? "bg-[#C8A96E] text-white"
                  : "text-slate-500 hover:bg-slate-50 hover:text-slate-700"
              }`}
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center"><i className={t.icon} /></span>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── TAB: DISEÑO ──────────────────────────────────────── */}
        {tab === "template" && (
          <div className="space-y-5">
            {/* Mode */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-[#0f172a] font-semibold text-sm mb-4">Tipo de Diseño</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { key: "template",  label: "Template Genérico",   desc: "Fondo del catálogo",    icon: "ri-palette-line" },
                  { key: "photo",     label: "Foto Propia",          desc: "Tu foto como fondo",    icon: "ri-image-line" },
                  { key: "custom",    label: "Template Personalizado", desc: "Diseño exclusivo",    icon: "ri-vip-crown-line" },
                ].map((m) => (
                  <button
                    key={m.key}
                    onClick={() => hc({ design_mode: m.key })}
                    className={`rounded-xl border p-4 text-left transition-all cursor-pointer ${
                      form.design_mode === m.key
                        ? "border-[#C8A96E] bg-[#C8A96E]/5"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${form.design_mode === m.key ? "bg-[#C8A96E]/15 text-[#C8A96E]" : "bg-slate-50 text-slate-400"}`}>
                      <i className={m.icon}></i>
                    </div>
                    <p className={`text-sm font-semibold ${form.design_mode === m.key ? "text-[#C8A96E]" : "text-[#0f172a]"}`}>{m.label}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{m.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Generic templates */}
            {form.design_mode === "template" && (
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="text-[#0f172a] font-semibold text-sm mb-4">Selecciona un Fondo</h3>
                {genericTemplates.length === 0 ? (
                  <p className="text-slate-400 text-sm text-center py-8">Tu fotógrafo no ha configurado templates aún.</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                    {genericTemplates.map((tpl) => {
                      const thumb = tpl.preview_url || tpl.background_url;
                      const active = form.template_id === tpl.id;
                      return (
                        <button
                          key={tpl.id}
                          onClick={() => hc({ template_id: tpl.id, text_color: tpl.text_color || "#ffffff" })}
                          className={`rounded-xl border overflow-hidden text-left transition-all cursor-pointer ${active ? "border-[#C8A96E] ring-2 ring-[#C8A96E]/30" : "border-slate-200 hover:border-slate-300"}`}
                        >
                          <div className="relative h-24 bg-slate-100">
                            {thumb ? <img src={thumb} alt={tpl.name} className="w-full h-full object-cover object-top" /> : <div className="w-full h-full flex items-center justify-center text-slate-300"><i className="ri-image-line text-2xl"></i></div>}
                            {active && <div className="absolute inset-0 flex items-center justify-center bg-[#C8A96E]/20"><div className="w-8 h-8 rounded-full bg-[#C8A96E] flex items-center justify-center"><i className="ri-check-line text-white text-sm"></i></div></div>}
                          </div>
                          <div className="p-2.5">
                            <p className={`text-xs font-medium truncate ${active ? "text-[#C8A96E]" : "text-[#0f172a]"}`}>{tpl.name}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Photo mode */}
            {form.design_mode === "photo" && (
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="text-[#0f172a] font-semibold text-sm mb-4">Tu Foto de Portada</h3>
                {form.photo_url || form.cover_image_url ? (
                  <div className="relative rounded-xl overflow-hidden mb-3 h-64">
                    <img src={form.photo_url || form.cover_image_url || ""} alt="Portada" className="w-full h-full object-cover object-top" />
                    <button
                      onClick={() => hc({ photo_url: null, cover_image_url: null })}
                      className="absolute top-3 right-3 w-8 h-8 rounded-full bg-red-500/90 text-white flex items-center justify-center text-xs hover:bg-red-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  </div>
                ) : (
                  <div onClick={() => coverInputRef.current?.click()} className="border-2 border-dashed border-slate-200 rounded-xl p-10 text-center hover:border-[#C8A96E]/40 transition-all cursor-pointer">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400">
                      <i className="ri-upload-cloud-2-line text-xl"></i>
                    </div>
                    <p className="text-sm text-slate-500">Subir foto de portada</p>
                    <p className="text-xs text-slate-400 mt-1">JPG, PNG — máx 5MB</p>
                  </div>
                )}
                <input
                  ref={coverInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setUploadingCover(true);
                    const url = await uploadFile(file, "photo");
                    setUploadingCover(false);
                    if (url) hc({ photo_url: url, cover_image_url: url });
                    else Swal.fire({ title: "Error al subir", icon: "error", confirmButtonColor: "#C8A96E", background: "#fff", color: "#0f172a" });
                  }}
                />
                {uploadingCover && <p className="text-xs text-[#C8A96E] mt-2 flex items-center gap-1"><i className="ri-loader-4-line animate-spin" /> Subiendo…</p>}
                {(form.photo_url || form.cover_image_url) && (
                  <button onClick={() => coverInputRef.current?.click()} className="mt-3 text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1.5 cursor-pointer">
                    <i className="ri-refresh-line"></i> Cambiar foto
                  </button>
                )}
              </div>
            )}

            {/* Custom template */}
            {form.design_mode === "custom" && (
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="text-[#0f172a] font-semibold text-sm mb-4">Template Personalizado</h3>
                {clientTemplates.length > 0 ? (
                  <div>
                    <p className="text-xs text-slate-400 mb-3">Tu fotógrafo te asignó estos templates exclusivos:</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {clientTemplates.map((ct) => {
                        const thumb = ct.preview_url || ct.template_url;
                        const active = form.custom_template_id === ct.id;
                        return (
                          <button
                            key={ct.id}
                            onClick={() => hc({ custom_template_id: ct.id, custom_template_url: ct.template_url })}
                            className={`rounded-xl border overflow-hidden text-left transition-all cursor-pointer ${active ? "border-[#C8A96E] ring-2 ring-[#C8A96E]/30" : "border-slate-200 hover:border-slate-300"}`}
                          >
                            <div className="relative h-24 bg-slate-100">
                              {thumb ? <img src={thumb} alt={ct.name} className="w-full h-full object-cover object-top" /> : <div className="w-full h-full flex items-center justify-center text-slate-300"><i className="ri-vip-crown-line text-2xl"></i></div>}
                              {active && <div className="absolute inset-0 flex items-center justify-center bg-[#C8A96E]/20"><div className="w-8 h-8 rounded-full bg-[#C8A96E] flex items-center justify-center"><i className="ri-check-line text-white text-sm"></i></div></div>}
                            </div>
                            <div className="p-2.5">
                              <p className={`text-xs font-medium truncate ${active ? "text-[#C8A96E]" : "text-[#0f172a]"}`}>{ct.name}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 border border-dashed border-slate-200 rounded-xl">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-slate-50 flex items-center justify-center text-slate-300">
                      <i className="ri-vip-crown-line text-xl"></i>
                    </div>
                    <p className="text-slate-500 text-sm">Tu fotógrafo no te ha asignado templates personalizados aún.</p>
                    <p className="text-slate-400 text-xs mt-1">Contáctalos para solicitar un diseño exclusivo.</p>
                  </div>
                )}
                {/* Manual URL fallback */}
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <label className="block text-xs text-slate-400 mb-1.5">URL de template personalizado (opcional)</label>
                  <div className="flex gap-2">
                    <input
                      ref={customTplInputRef as React.RefObject<HTMLInputElement>}
                      type="url"
                      value={form.custom_template_url ?? ""}
                      onChange={(e) => hc({ custom_template_url: e.target.value || null })}
                      placeholder="https://…"
                      className={INPUT}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── TAB: EVENTO ──────────────────────────────────────── */}
        {tab === "event" && (
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
            <h3 className="text-[#0f172a] font-semibold text-sm mb-1">Datos del Evento</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={LABEL}>Nombre del festejado / pareja</label>
                <input type="text" value={form.event_title || ""} onChange={(e) => hc({ event_title: e.target.value })} placeholder="Ana & Carlos" className={INPUT} />
              </div>
              <div>
                <label className={LABEL}>Anfitriones</label>
                <input type="text" value={form.host_names || ""} onChange={(e) => hc({ host_names: e.target.value })} placeholder="Sr. y Sra. García" className={INPUT} />
              </div>
              <div>
                <label className={LABEL}>Tipo de evento</label>
                <select value={form.event_type} onChange={(e) => hc({ event_type: e.target.value })} className={INPUT}>
                  {["Wedding","XV Años","Baptism","Graduation","Birthday","Baby Shower","Engagement","Anniversary","Corporate Event","Other"].map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={LABEL}>Fecha</label>
                <input type="date" value={form.event_date?.split("T")[0] || ""} onChange={(e) => hc({ event_date: e.target.value ? new Date(e.target.value).toISOString() : null })} className={INPUT} />
              </div>
              <div>
                <label className={LABEL}>Hora del evento</label>
                <input type="text" value={form.event_time || ""} onChange={(e) => hc({ event_time: e.target.value })} placeholder="7:00 PM" className={INPUT} />
              </div>
              <div>
                <label className={LABEL}>Código de vestimenta</label>
                <input type="text" value={form.dress_code || ""} onChange={(e) => hc({ dress_code: e.target.value })} placeholder="Formal / Etiqueta" className={INPUT} />
              </div>
              <div className="sm:col-span-2">
                <label className={LABEL}>Nombre del lugar</label>
                <input type="text" value={form.venue_name || ""} onChange={(e) => hc({ venue_name: e.target.value })} placeholder="Jardín El Encanto" className={INPUT} />
              </div>
              <div className="sm:col-span-2">
                <label className={LABEL}>Dirección</label>
                <textarea value={form.venue_address || ""} onChange={(e) => hc({ venue_address: e.target.value })} placeholder="Av. Principal 123, Ciudad" rows={2} className={`${INPUT} resize-none`} />
              </div>
              <div className="sm:col-span-2">
                <label className={LABEL}>Google Maps URL</label>
                <input type="url" value={form.google_maps_url || ""} onChange={(e) => hc({ google_maps_url: e.target.value })} placeholder="https://maps.google.com/…" className={INPUT} />
              </div>
              <div className="sm:col-span-2">
                <label className={LABEL}>Mesa de regalos (URL)</label>
                <input type="url" value={form.gift_registry_url || ""} onChange={(e) => hc({ gift_registry_url: e.target.value })} placeholder="https://…" className={INPUT} />
              </div>
            </div>
          </div>
        )}

        {/* ── TAB: CEREMONIA ──────────────────────────────────── */}
        {tab === "ceremony" && (
          <div className="space-y-5">
            {/* Ceremonia */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center">
                  <i className="ri-map-pin-line text-rose-500 text-sm"></i>
                </div>
                <h3 className="text-[#0f172a] font-semibold text-sm">Ceremonia</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={LABEL}>Nombre del lugar</label>
                  <input type="text" value={form.ceremony_name || ""} onChange={(e) => hc({ ceremony_name: e.target.value })} placeholder="Iglesia San José" className={INPUT} />
                </div>
                <div>
                  <label className={LABEL}>Hora de ceremonia</label>
                  <input type="text" value={form.ceremony_time || ""} onChange={(e) => hc({ ceremony_time: e.target.value })} placeholder="5:30 PM" className={INPUT} />
                </div>
                <div className="sm:col-span-2">
                  <label className={LABEL}>Dirección</label>
                  <textarea value={form.ceremony_address || ""} onChange={(e) => hc({ ceremony_address: e.target.value })} rows={2} placeholder="Dirección completa" className={`${INPUT} resize-none`} />
                </div>
                <div className="sm:col-span-2">
                  <label className={LABEL}>Google Maps URL</label>
                  <input type="url" value={form.ceremony_maps_url || ""} onChange={(e) => hc({ ceremony_maps_url: e.target.value })} placeholder="https://maps.google.com/…" className={INPUT} />
                </div>
              </div>
            </div>

            {/* Recepción */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                  <i className="ri-restaurant-line text-amber-500 text-sm"></i>
                </div>
                <h3 className="text-[#0f172a] font-semibold text-sm">Recepción</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className={LABEL}>Nombre del salón</label>
                  <input type="text" value={form.reception_name || ""} onChange={(e) => hc({ reception_name: e.target.value })} placeholder="Jardín El Encanto" className={INPUT} />
                </div>
                <div className="sm:col-span-2">
                  <label className={LABEL}>Dirección</label>
                  <textarea value={form.reception_address || ""} onChange={(e) => hc({ reception_address: e.target.value })} rows={2} placeholder="Dirección completa" className={`${INPUT} resize-none`} />
                </div>
                <div className="sm:col-span-2">
                  <label className={LABEL}>Google Maps URL</label>
                  <input type="url" value={form.reception_maps_url || ""} onChange={(e) => hc({ reception_maps_url: e.target.value })} placeholder="https://maps.google.com/…" className={INPUT} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── TAB: MÚSICA ──────────────────────────────────────── */}
        {tab === "music" && (
          <div className="space-y-5">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-[#0f172a] font-semibold text-sm mb-4">Música de Fondo</h3>

              {/* Current music */}
              {(form.music_url || form.client_music_url) && (
                <div className="mb-4">
                  <MusicPlayer url={form.music_url || form.client_music_url || ""} title={form.music_title || form.client_music_name} />
                  <button onClick={() => hc({ music_url: null, client_music_url: null, music_title: null, client_music_name: null })} className="mt-2 text-xs text-red-400 hover:text-red-600 flex items-center gap-1 cursor-pointer">
                    <i className="ri-delete-bin-line"></i> Quitar música
                  </button>
                </div>
              )}

              {/* Catalog */}
              {musicCatalog.length > 0 && (
                <div className="mb-5">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Catálogo del estudio</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {musicCatalog.map((m) => {
                      const active = form.music_url === m.music_url;
                      return (
                        <button
                          key={m.id}
                          onClick={() => hc({ music_url: m.music_url, music_title: m.name, client_music_url: null, client_music_name: null })}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-all cursor-pointer ${active ? "border-[#C8A96E] bg-[#C8A96E]/5" : "border-slate-200 hover:border-slate-300"}`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${active ? "bg-[#C8A96E] text-white" : "bg-slate-100 text-slate-400"}`}>
                            <i className={active ? "ri-pause-fill text-sm" : "ri-music-line text-sm"}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs font-medium truncate ${active ? "text-[#C8A96E]" : "text-[#0f172a]"}`}>{m.name}</p>
                            <p className="text-[10px] text-slate-400 truncate">{m.artist || m.genre || ""}{m.duration ? ` · ${m.duration}` : ""}</p>
                          </div>
                          {active && <i className="ri-check-line text-[#C8A96E] shrink-0"></i>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Upload own music */}
              <div className="border-t border-slate-100 pt-4">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">O sube tu propia música</p>
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:border-[#C8A96E]/40 hover:text-[#C8A96E] transition-all">
                    <i className="ri-upload-2-line"></i>
                    Subir archivo de audio
                    <input type="file" accept="audio/*" className="hidden" ref={musicInputRef} onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setUploadingMusic(true);
                      const url = await uploadFile(file, "music");
                      setUploadingMusic(false);
                      if (url) hc({ client_music_url: url, client_music_name: file.name, music_url: url, music_title: file.name });
                    }} />
                  </label>
                  {uploadingMusic && <span className="text-xs text-[#C8A96E] flex items-center gap-1"><i className="ri-loader-4-line animate-spin" /> Subiendo…</span>}
                </div>
                <p className="text-xs text-slate-400 mt-2">Formatos: MP3, OGG, AAC — máx 10MB</p>
              </div>

              {/* Custom URL */}
              <div className="border-t border-slate-100 pt-4">
                <label className={LABEL}>O pega un enlace directo de música</label>
                <input
                  type="url"
                  value={form.music_url || ""}
                  onChange={(e) => hc({ music_url: e.target.value || null, music_title: e.target.value ? (form.music_title || "Mi música") : null })}
                  placeholder="https://… (MP3 directo)"
                  className={INPUT}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── TAB: MENSAJES ────────────────────────────────────── */}
        {tab === "message" && (
          <div className="space-y-5">
            <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#0f172a] mb-1">Mensaje de bienvenida</label>
                <p className="text-xs text-slate-400 mb-3">Aparece al inicio de la invitación.</p>
                <textarea value={form.welcome_message || ""} onChange={(e) => hc({ welcome_message: e.target.value })} rows={4} maxLength={500} placeholder="Escribí un mensaje cálido para tus invitados…" className={`${INPUT} resize-none`} />
                <p className="text-right text-xs text-slate-300 mt-1">{form.welcome_message?.length || 0}/500</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#0f172a] mb-1">Mensaje de agradecimiento</label>
                <p className="text-xs text-slate-400 mb-3">Aparece al final de la invitación.</p>
                <textarea value={form.thank_you_message || ""} onChange={(e) => hc({ thank_you_message: e.target.value })} rows={4} maxLength={500} placeholder="¡Gracias por acompañarnos en este día especial!" className={`${INPUT} resize-none`} />
                <p className="text-right text-xs text-slate-300 mt-1">{form.thank_you_message?.length || 0}/500</p>
              </div>
            </div>
          </div>
        )}

        {/* ── TAB: COMPARTIR ───────────────────────────────────── */}
        {tab === "share" && (
          <div className="space-y-5">
            {/* Publish toggle */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#0f172a] font-semibold text-sm">Publicar invitación</p>
                  <p className="text-slate-400 text-xs mt-0.5">Cualquier persona con el link podrá verla</p>
                </div>
                <button
                  onClick={handlePublishToggle}
                  className={`relative w-12 h-7 rounded-full transition-colors cursor-pointer ${form.is_published ? "bg-[#C8A96E]" : "bg-slate-200"}`}
                >
                  <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-all ${form.is_published ? "left-5" : "left-0.5"}`} />
                </button>
              </div>
              {form.is_published && (
                <div className="mt-3 flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 text-xs text-emerald-700">
                  <i className="ri-checkbox-circle-fill"></i>
                  Tu invitación está en vivo
                </div>
              )}
            </div>

            {/* Link */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-[#0f172a] font-semibold text-sm mb-3">Link para compartir</h3>
              <div className="flex items-center gap-2">
                <div className="flex-1 px-3 py-2.5 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-500 font-mono truncate text-xs">
                  {shareUrl || "Publica primero para obtener el link"}
                </div>
                {shareUrl && (
                  <button
                    onClick={() => { navigator.clipboard.writeText(shareUrl); Swal.fire({ title: "¡Copiado!", icon: "success", confirmButtonColor: "#C8A96E", background: "#fff", color: "#0f172a", timer: 1000, showConfirmButton: false }); }}
                    className="shrink-0 flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:border-[#C8A96E]/40 hover:text-[#C8A96E] transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-clipboard-line"></i> Copiar
                  </button>
                )}
              </div>
            </div>

            {/* QR */}
            {shareUrl && (
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="text-[#0f172a] font-semibold text-sm mb-4">Código QR</h3>
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  <QRCodeDisplay url={shareUrl} size={180} />
                  <div className="space-y-3 text-center sm:text-left">
                    <p className="text-slate-500 text-sm">Imprime o comparte este QR — tus invitados lo escanean para abrir la invitación al instante.</p>
                    <a
                      href={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(shareUrl)}&format=png&margin=10`}
                      download="qr-invitacion.png"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-600 hover:border-[#C8A96E]/40 hover:text-[#C8A96E] transition-all cursor-pointer"
                    >
                      <i className="ri-download-2-line"></i> Descargar QR
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Quick share */}
            {shareUrl && (
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="text-[#0f172a] font-semibold text-sm mb-4">Compartir rápido</h3>
                <div className="flex flex-wrap gap-3">
                  <a href={`https://wa.me/?text=${encodeURIComponent(`${form.event_title || form.title} — ${shareUrl}`)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-50 text-emerald-700 text-sm font-medium hover:bg-emerald-100 transition-colors cursor-pointer">
                    <i className="ri-whatsapp-line"></i> WhatsApp
                  </a>
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-50 text-blue-700 text-sm font-medium hover:bg-blue-100 transition-colors cursor-pointer">
                    <i className="ri-facebook-circle-line"></i> Facebook
                  </a>
                  <a href={`mailto:?subject=${encodeURIComponent(form.event_title || form.title)}&body=${encodeURIComponent(`Te invito a ver mi invitación: ${shareUrl}`)}`} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-50 text-slate-700 text-sm font-medium hover:bg-slate-100 transition-colors cursor-pointer">
                    <i className="ri-mail-line"></i> Email
                  </a>
                  <a href={`/invite/${form.public_slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#C8A96E]/10 text-[#C8A96E] text-sm font-medium hover:bg-[#C8A96E]/20 transition-colors cursor-pointer">
                    <i className="ri-external-link-line"></i> Ver invitación
                  </a>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── TAB: INVITADOS ───────────────────────────────────── */}
        {tab === "guests" && (
          <div className="space-y-5">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: "Confirmados", count: yesCount, icon: "ri-checkbox-circle-fill", color: "text-emerald-600", bg: "bg-emerald-50" },
                { label: "No asisten",  count: noCount,  icon: "ri-close-circle-fill",    color: "text-red-500",     bg: "bg-red-50" },
                { label: "Tal vez",     count: maybeCount, icon: "ri-question-fill",       color: "text-amber-500",   bg: "bg-amber-50" },
              ].map((s) => (
                <div key={s.label} className={`${s.bg} rounded-xl p-4 text-center`}>
                  <div className={`w-8 h-8 mx-auto mb-1.5 flex items-center justify-center ${s.color}`}>
                    <i className={`${s.icon} text-xl`}></i>
                  </div>
                  <p className={`text-2xl font-bold ${s.color}`}>{s.count}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Show guests toggle */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-[#0f172a]">Mostrar lista de invitados</p>
                <p className="text-xs text-slate-400 mt-0.5">Los asistentes podrán ver quién más viene</p>
              </div>
              <button
                onClick={() => hc({ show_guests: !form.show_guests })}
                className={`relative w-10 h-6 rounded-full transition-colors cursor-pointer ${form.show_guests ? "bg-[#C8A96E]" : "bg-slate-200"}`}
              >
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${form.show_guests ? "left-4" : "left-0.5"}`} />
              </button>
            </div>

            {/* RSVP list */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-[#0f172a] font-semibold text-sm">Respuestas recibidas</h3>
                <span className="text-xs text-slate-400">{rsvps.length} total</span>
              </div>
              {rsvps.length === 0 ? (
                <div className="p-10 text-center">
                  <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center mx-auto mb-3 text-slate-300">
                    <i className="ri-inbox-line text-2xl"></i>
                  </div>
                  <p className="text-slate-500 text-sm">Sin respuestas aún</p>
                  <p className="text-slate-400 text-xs mt-1">Comparte el link y empieza a recibir confirmaciones.</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-50">
                  {rsvps.map((r) => (
                    <div key={r.id} className="px-5 py-4 flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${r.attendance_status === "yes" ? "bg-emerald-50 text-emerald-600" : r.attendance_status === "no" ? "bg-red-50 text-red-500" : "bg-amber-50 text-amber-500"}`}>
                        <i className={r.attendance_status === "yes" ? "ri-checkbox-circle-line" : r.attendance_status === "no" ? "ri-close-circle-line" : "ri-question-line"}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <p className="text-sm font-medium text-[#0f172a]">{r.guest_name}</p>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${r.attendance_status === "yes" ? "bg-emerald-50 text-emerald-700" : r.attendance_status === "no" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600"}`}>
                            {r.attendance_status === "yes" ? `Asiste${r.guest_count > 1 ? ` +${r.guest_count - 1}` : ""}` : r.attendance_status === "no" ? "No asiste" : "Tal vez"}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400">{r.phone || r.email || ""}</p>
                        {r.message && <p className="text-xs text-slate-500 mt-1.5 bg-slate-50 rounded-lg px-3 py-2 italic">{r.message}</p>}
                      </div>
                      <p className="text-xs text-slate-300 shrink-0 whitespace-nowrap">{new Date(r.created_at).toLocaleDateString("es-MX")}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Save bar */}
        <div className="sticky bottom-0 bg-white/90 backdrop-blur-sm border-t border-slate-200 -mx-4 md:-mx-6 px-4 md:px-6 py-3 flex items-center justify-between">
          <p className={`text-xs ${dirty ? "text-[#C8A96E]" : "text-slate-400"}`}>
            {dirty ? "Cambios sin guardar" : "Todo guardado"}
          </p>
          <button
            onClick={handleSave}
            disabled={!dirty || saving}
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-white bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
          >
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}